import { useEffect, useRef, type RefObject } from 'react';
import type { ItemDto } from '../api/wardrobe';
import { itemIconPath } from '../compositor/assetPaths';
import { slotCaption } from './slotGroups';

export type WornPiece = {
  slot: string;
  item: ItemDto;
};

export type WornStripProps = {
  /** Every worn, non-projected piece across EVERY category, in the order the dock reads. */
  pieces: WornPiece[];
  /** The slot whose actions are open, or null. Owned by the screen because it is the same focus
   *  slot Dye already acts on — two sources of "which piece" would drift. */
  openSlot: string | null;
  onToggle: (slot: string) => void;
  onDye: (piece: WornPiece) => void;
  onTakeOff: (piece: WornPiece) => void;
  disabled: boolean;
  /** The dye sheet restores focus to its trigger on close (BottomSheet's `restoreFocusRef`); the
   *  actions row stays mounted while the sheet is open, which is what makes the ref still resolve. */
  dyeButtonRef?: RefObject<HTMLButtonElement>;
};

/**
 * SCREENS.md "/wardrobe — worn strip" (2026-09-14). One chip per piece the companion is actually
 * wearing, across every category, with that piece's own actions behind it.
 *
 * THE THREE THINGS THIS EXISTS FOR, none of which the screen could do before:
 *  1. WHAT IS ON. The dock's count badges say "2 pieces in Body" but never which, and seeing the
 *     whole outfit meant walking all five categories. The pet itself is not a substitute: an
 *     `aura_back` or a dyed under-layer can be entirely hidden behind what is over it.
 *  2. TAKING SOMETHING OFF. The only way to unequip was to find the piece's own cell in the grid
 *     and tap it a second time — a toggle with nothing anywhere on screen naming it, in a
 *     category the player may have to switch to first. "Take off" is now a word you can read.
 *  3. DYE (and, until the 3D wardrobe retired it (W3), Adjust). These used to float in the preview's top control row as "Dye <item name>",
 *     between the identity plate and Looks, at the 412 viewport. Measured there: "Adjust Fern
 *     Cloak" already spanned 172 of the 412 available; the pack ships "The Lantern-Bearer's Starweave Shroud",
 *     which cannot fit at any weight — and the control changed its own label on every tap, which
 *     is what made the overflow unpredictable rather than merely tight. Attached to the piece
 *     instead, the name is the chip's own context and the buttons are verbs of fixed width.
 *
 * A DISCLOSURE, NOT A FLOATING POPOVER. The demo this was approved from
 * (`docs/demos/wardrobe-polish-demo.html`) drew a popover hanging off the chip. A popover here
 * would need a focus trap, an outside-press handler and collision handling against a scrolling
 * grid — Radix's `Popover`, which this app does not otherwise use — to solve a problem the
 * drawer already has a pattern for: `aria-expanded` on the trigger and the verbs beside it,
 * exactly the filter disclosure two lines up. It also cannot cover the first grid row, which a
 * popover anchored to an --sp-6 chip inevitably does.
 *
 * ONE ROW, AND OPEN ON ARRIVAL (2026-09-14, evening). The first cut put the verbs on a second row
 * under the chips, CLOSED until a chip was pressed. The owner's first report on it: "resizing of
 * wardrobe item is missing now. no option to resize." Nothing was missing — Adjust was one tap
 * away, behind a thumbnail with a caption that reads as a label — but a permanent affordance
 * (the preview row's "Adjust <item>", there since the free-transform wardrobe landed) had become
 * a hidden one, and that is a regression however tidy the row. So the screen now opens the
 * active category's worn piece itself (see `WardrobeScreen`'s `wornOpenSlot`) and the verbs sit
 * in the SAME row as the chips: a second row that is always open would cost the grid a row of
 * cells permanently — measured at 412×839, the grid kept 99 above the dock with the two-row
 * layout open, one row of cells; at 412×700 it would have kept none. The piece's full name left
 * with that row: the ringed chip is the verbs' context, the chip's accessible name carries it for
 * assistive tech, and the grid cell — ringed too, when its category is showing — spells it out.
 */
export function WornStrip({
  pieces, openSlot, onToggle, onDye, onTakeOff, disabled, dyeButtonRef,
}: WornStripProps) {
  const open = pieces.find((p) => p.slot === openSlot) ?? null;
  const actionsRef = useRef<HTMLDivElement>(null);
  const lastOpened = useRef<string | null>(null);
  // Set by a chip press, consumed by the effect below: the screen also opens a piece on its OWN
  // (the active category's worn piece, on arrival and on every dock switch), and an open nobody
  // pressed for must not take focus — on a fresh load that would yank it out of wherever the
  // router put it, into a button in the middle of the drawer.
  const pressed = useRef(false);

  // Move focus into the actions when a PRESS opens them — they are a row of buttons that appeared
  // below the control that was just pressed, and a keyboard user would otherwise have to tab
  // through the rest of the strip to reach them. Only on the transition, never on re-render, or
  // every commit would steal focus back from whatever the player pressed next.
  useEffect(() => {
    if (open && lastOpened.current !== open.slot && pressed.current) {
      actionsRef.current?.querySelector('button')?.focus();
    }
    pressed.current = false;
    lastOpened.current = open?.slot ?? null;
  }, [open]);

  if (pieces.length === 0) return null;

  return (
    <div className="worn-strip">
      <div className="worn-strip-row" role="group" aria-label="Wearing">
        <span className="worn-strip-label" aria-hidden="true">Wearing</span>
        {/* The chips scroll; the verbs for the open chip are pinned at the row's end, so they are
            on screen whichever chip is open and however many there are. */}
        <ul className="worn-chips">
          {pieces.map((p) => (
            <li key={p.slot}>
              <button
                type="button"
                className={`worn-chip${p.slot === openSlot ? ' worn-chip-open' : ''}`}
                aria-expanded={p.slot === openSlot}
                aria-label={`${p.item.name} — ${slotCaption(p.slot)}`}
                onClick={() => { pressed.current = true; onToggle(p.slot); }}
              >
                <span className="worn-chip-thumb">
                  {/* A parked icon 404s; the glyph Chromium draws for it is not a thumbnail. */}
                  <img src={itemIconPath(p.item.code)} alt="" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                </span>
                <span className="worn-chip-caption" aria-hidden="true">{slotCaption(p.slot)}</span>
              </button>
            </li>
          ))}
        </ul>
        {open && (
        <div className="worn-actions" ref={actionsRef}>
          {/* Take off and Dye only: Adjust went with the 2D model (W3) — a 3D garment is built
              onto the pet, so there is nothing to nudge. */}
          {open.item.dyeChannels > 0 && (
            <button ref={dyeButtonRef} type="button" className="btn-quiet" disabled={disabled} onClick={() => onDye(open)}>
              Dye
            </button>
          )}
          <button
            type="button"
            className="btn-quiet worn-take-off"
            disabled={disabled}
            onClick={() => onTakeOff(open)}
          >
            Take off
          </button>
        </div>
        )}
      </div>
    </div>
  );
}
